import React, { Suspense, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { LogOut, ShoppingBag, UserRound } from "lucide-react";
import { getAsset } from "../../helpers/helper";
import {
  selectCurrentToken,
  selectCurrentUser,
  selectIsAuthenticated,
} from "../../store/slices/authSlice";
import userAvatarImg from "../../assets/images/dummy/user-avatar-img.jpg";
import "./style.css";

const Logoutmodal = React.lazy(() => import("../Logout_modal/Logoutmodal"));

const NAV_LINKS = [
  { label: "Home", to: "/", end: true },
  { label: "Shop Showcase", to: "/showcase" },
  { label: "Marketing Agent", to: "/marketing-agent" },
  { label: "Hire Agent", to: "/hire-marketing-agent" },
  { label: "Pricing", to: "/pricing" },
  { label: "Benefits", to: "/selectezi-benefits" },
  { label: "Investment Opportunities", to: "/investment-opportunities" },
  { label: "Guideline", to: "/guideline" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const profileMenuRef = useRef(null);
  const legacyUser = useSelector((state) => state.user?.name);
  const authUser = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const navigate = useNavigate();
  const currentUser = legacyUser || authUser;
  const isLoggedIn = Boolean(isAuthenticated || token);
  const avatarSource =
    currentUser?.imageUrl ||
    currentUser?.avatar ||
    currentUser?.profileImage ||
    currentUser?.image;
  const avatarUrl = avatarSource ? getAsset(avatarSource) : userAvatarImg;
  const displayName =
    currentUser?.fullname ||
    currentUser?.name ||
    currentUser?.username ||
    "My account";

  useEffect(() => {
    setAvatarFailed(false);
  }, [avatarUrl]);

  useEffect(() => {
    const closeProfileMenu = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsProfileOpen(false);
    };

    document.addEventListener("pointerdown", closeProfileMenu);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeProfileMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  // Desktop width par aate hi mobile menu / profile menu band kar do
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1280px)");
    const onChange = (e) => {
      if (e.matches) {
        setIsOpen(false);
        setIsProfileOpen(false);
      }
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <header className="sz-navbar">
      <div className="container sz-navbar-inner">
        <NavLink to="/" className="sz-logo">
          SELECTEZI
        </NavLink>

        <div className="sz-navbar-right-controls d-flex align-items-center gap-2">
          <button
            type="button"
            className="sz-navbar-toggle"
            aria-label="Toggle navigation"
            aria-expanded={isOpen}
            onClick={() => setIsOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <nav className={`sz-nav-links ${isOpen ? "sz-nav-links--open" : ""}`}>
          <ul>
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    "sz-nav-link" + (isActive ? " sz-nav-link--active" : "")
                  }
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>

          {isLoggedIn ? (
            <div className="sz-nav-actions" ref={profileMenuRef}>
              <button
                type="button"
                className="sz-profile-toggle border-0 rounded-0"
                aria-expanded={isProfileOpen}
                aria-haspopup="menu"
                onClick={() => setIsProfileOpen((open) => !open)}
              >
                <img
                  className="sz-profile-avatar"
                  src={avatarFailed ? userAvatarImg : avatarUrl}
                  alt=""
                  onError={() => setAvatarFailed(true)}
                />
                <span className="sz-profile-name">{displayName}</span>
                <span
                  className={`sz-profile-chevron${isProfileOpen ? " sz-profile-chevron--open" : ""}`}
                  aria-hidden="true"
                />
              </button>

              <div
                className={`sz-profile-menu rounded-1 border-0 ${isProfileOpen ? " sz-profile-menu--open" : ""}`}
                role="menu"
                aria-hidden={!isProfileOpen}
              >
                <div className="sz-profile-menu-header">
                  <img
                    className="sz-profile-menu-avatar"
                    src={avatarFailed ? userAvatarImg : avatarUrl}
                    alt=""
                    onError={() => setAvatarFailed(true)}
                  />
                  <div className="sz-profile-menu-user">
                    <strong>{displayName}</strong>
                    {currentUser?.email && <span>{currentUser.email}</span>}
                  </div>
                </div>
                <span className="sz-profile-menu-label fw-medium">
                  Your account
                </span>
                <NavLink
                  to="/BuyingSide"
                  role="menuitem"
                  onClick={() => {
                    setIsProfileOpen(false);
                    setIsOpen(false);
                  }}
                  className="fw-medium"
                >
                  <ShoppingBag aria-hidden="true" />
                  Buying Side
                </NavLink>
                <NavLink
                  to="/user-profile"
                  role="menuitem"
                  onClick={() => {
                    setIsProfileOpen(false);
                    setIsOpen(false);
                  }}
                  className="fw-medium"
                >
                  <UserRound aria-hidden="true" />
                  My Profile
                </NavLink>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setIsProfileOpen(false);
                    setIsOpen(false);
                    setShowLogout(true);
                  }}
                >
                  <LogOut aria-hidden="true" />
                  Log Out
                </button>
              </div>
            </div>
          ) : (
            <div className="sz-nav-actions">
              <button
                type="button"
                // onClick={() => navigate("/login")}
                className="sz-nav-signin"
              >
                Sign In
              </button>
              <button
                type="button"
                // onClick={() => navigate("/signup")}
                className="sz-btn sz-btn-primary py-2 rounded sz-nav-register"
              >
                Sign Up
              </button>
            </div>
          )}
        </nav>
      </div>
      {showLogout && (
        <Suspense fallback={null}>
          <Logoutmodal
            show={showLogout}
            handleClose={() => setShowLogout(false)}
          />
        </Suspense>
      )}
    </header>
  );
}
